import { useCommonRouter } from '@repo/platform';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AFTER_CI_QUERY_STATE, PASS_ENTRY_HISTORY_LENGTH, PASS_EXIT_HISTORY_LENGTH } from '../constants';
import { isPreventRouteChangeAtom } from '../store';

export const useCiHistoryController = (mode: 'entry' | 'exit' | 'none') => {
  const router = useCommonRouter();
  const isPreventPopstateEvent = useRef(false);
  const historyPassCount = useRef(0);
  const setIsPreventRouteChange = useSetAtom(isPreventRouteChangeAtom);
  const [queryState, setQueryState] = useState<string | null>(null);

  const setHistoryPassCount = () => {
    const entryHistoryIndex = Number(sessionStorage.getItem(PASS_ENTRY_HISTORY_LENGTH));
    const exitHistoryIndex = Number(sessionStorage.getItem(PASS_EXIT_HISTORY_LENGTH));

    if (!entryHistoryIndex || !exitHistoryIndex || entryHistoryIndex >= exitHistoryIndex) {
      historyPassCount.current = 6;
      return;
    }
    historyPassCount.current = exitHistoryIndex - entryHistoryIndex;
  };

  const removeHistoryLengthStorage = () => {
    sessionStorage.removeItem(PASS_ENTRY_HISTORY_LENGTH);
    sessionStorage.removeItem(PASS_EXIT_HISTORY_LENGTH);
  };

  const performHistoryBack = useCallback((skipCount: number) => {
    window.history.go(-skipCount);
  }, []);

  const softButtonHistoryBack = () => {
    isPreventPopstateEvent.current = true;
    if (queryState === AFTER_CI_QUERY_STATE) {
      performHistoryBack(historyPassCount.current + 1);
    } else {
      router.back();
    }
    removeHistoryLengthStorage();
  };

  const popstateEvent = useCallback(() => {
    if (isPreventPopstateEvent.current) return;
    if (queryState === AFTER_CI_QUERY_STATE) {
      setIsPreventRouteChange(true);
      performHistoryBack(historyPassCount.current);
    }
    removeHistoryLengthStorage();
  }, [performHistoryBack, queryState, setIsPreventRouteChange]);

  const updateEntryHistoryLength = useCallback(() => {
    const entryHistoryIndex = sessionStorage.getItem(PASS_ENTRY_HISTORY_LENGTH);
    if (entryHistoryIndex || queryState === null || queryState === AFTER_CI_QUERY_STATE) return;

    sessionStorage.setItem(PASS_ENTRY_HISTORY_LENGTH, String(window.history.length));
  }, [queryState]);

  useEffect(() => {
    if (mode === 'exit' && queryState === AFTER_CI_QUERY_STATE) {
      const exitHistoryIndex = sessionStorage.getItem(PASS_EXIT_HISTORY_LENGTH);
      if (!exitHistoryIndex || Number(exitHistoryIndex) < window.history.length) {
        sessionStorage.setItem(PASS_EXIT_HISTORY_LENGTH, String(window.history.length));
      }
      setHistoryPassCount();
      window.addEventListener('popstate', popstateEvent);
    }

    return () => {
      window.removeEventListener('popstate', popstateEvent);
    };
  }, [popstateEvent, mode, queryState]);

  useEffect(() => {
    if (mode === 'entry') updateEntryHistoryLength();

    const params = new URLSearchParams(window.location.search);
    setQueryState(params.get('state') ?? '');
  }, [mode, updateEntryHistoryLength]);

  return { softButtonHistoryBack, updateEntryHistoryLength };
};
